import { Box, Typography, useMediaQuery, useTheme, keyframes } from "@mui/material";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { mobile, isMobileWebBrowser } from 'src/utils/platformUtils';

// Shimmer for "thinking": continuous back-and-forth (left→right→left), no cut at loop
const thinkingShimmer = keyframes`
  0%   { background-position: 100% 50%; }
  50%  { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
`;

/** Default thoughts: gray → white highlight → gray */
const SHIMMER_GRADIENT_DEFAULT =
  "linear-gradient(90deg, #9e9e9e 0%, #9e9e9e 25%, #b0b0b0 35%, #d0d0d0 42%, #fff 50%, #d0d0d0 58%, #b0b0b0 65%, #9e9e9e 75%, #9e9e9e 100%)";
/** 3D sign (clipContent): gray → black highlight → gray */
const SHIMMER_GRADIENT_SIGN =
  "linear-gradient(90deg, #9e9e9e 0%, #9e9e9e 25%, #757575 35%, #404040 42%, #000000 50%, #404040 58%, #757575 65%, #9e9e9e 75%, #9e9e9e 100%)";
// A small component to handle the display of a single thought.
const ThoughtItemCommon = ({
  text,
  allowWrap = false,
  /** Clip content (no scroll) — e.g. 3D sign panel */
  clipContent = false,
}: {
  text: string;
  allowWrap?: boolean;
  clipContent?: boolean;
}) => {
  //check if the device is mobile or tablet
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.between("xs", "lg"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")) || isMobileWebBrowser() || mobile;
  //max length of the text for thought
  const maxLength = 39;

  const truncateText = (inputText: string): string => {
    // If text is not provided or is within the length limit, return it.
    if (!inputText || inputText.length <= maxLength) {
      return inputText;
    }

    // Extract the actual content by removing ** from start and end
    let actualContent = inputText;
    let hasStartBold = false;
    let hasEndBold = false;

    // Check and remove ** from the beginning
    if (actualContent.startsWith("**")) {
      actualContent = actualContent.substring(2);
      hasStartBold = true;
    }

    // Check and remove ** from the end
    if (actualContent.endsWith("**")) {
      actualContent = actualContent.substring(0, actualContent.length - 2);
      hasEndBold = true;
    }

    // If the actual content (without **) is within the length limit, return original
    if (actualContent.length <= maxLength) {
      return inputText;
    }

    // Take the part of the actual content that is within the maxLength
    const truncated = actualContent.substring(0, maxLength);
    // find the last space in the truncated text
    const lastSpaceIndex = truncated.lastIndexOf(" ");

    let finalTruncated;
    // If a space is found, truncate at the last full word to avoid cutting words.
    if (lastSpaceIndex > 0) {
      finalTruncated = `${truncated.substring(0, lastSpaceIndex)}...`;
    } else {
      // If no space is found (one long word), hard truncate and add ellipsis.
      finalTruncated = `${actualContent.substring(0, maxLength - 3)}...`;
    }

    // Reconstruct the text with ** markers if they were present
    let result = finalTruncated;
    if (hasStartBold) {
      result = "**" + result;
    }
    if (hasEndBold) {
      result = result + "**";
    }

    return result;
  };

  // When allowWrap (e.g. dialog), show full text and wrap; otherwise truncate on mobile
  const displayText = allowWrap ? text : (isMobile ? truncateText(text) : text);

  const wrapStyles = allowWrap
    ? { whiteSpace: "normal" as const, overflow: clipContent ? ("hidden" as const) : ("visible" as const), textOverflow: "unset" as const }
    : { whiteSpace: "nowrap" as const, overflow: "hidden", textOverflow: "ellipsis" as const };

  const outerOverflow = clipContent ? "hidden" : allowWrap ? "auto" : "hidden";

  const shimmerGradient = clipContent ? SHIMMER_GRADIENT_SIGN : SHIMMER_GRADIENT_DEFAULT;

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 0,
        mb: 0,
        maxHeight: clipContent ? "100%" : undefined,
        overflow: outerOverflow,
        display: "flex",
        justifyContent: allowWrap && isMobile ? "flex-start" : "center",
        alignItems: "center",
        scrollbarWidth: "none",           // Firefox
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Box
        sx={{
          ...wrapStyles,
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <Typography
          lineHeight={allowWrap ? 1.2 : 1.3}
          sx={{
            color: clipContent ? "black" : "transparent",
            fontStyle: "italic",
            // textAlign: allowWrap ? "left" : "center",
            textAlign: "center",
            background: shimmerGradient,
            backgroundSize: "200% 100%",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            animation: `${thinkingShimmer} 2.4s ease-in-out infinite`,
            fontWeight: "bold",
            width: "100%",
            fontSize: clipContent ? "10px" : "14px",
            pl: clipContent ? 0 : 0.5,
            ...wrapStyles,

            "& p": {
              margin: 0,
              ...wrapStyles,
            },
            "& *": {
              color: "inherit",
              background: "inherit",
              backgroundClip: "inherit",
              WebkitBackgroundClip: "inherit",
              ...wrapStyles,
            },
          }}
        >
          {/* <ReactMarkdown remarkPlugins={[remarkGfm]}>Texto de prueba largo para ver el ancho</ReactMarkdown> */}
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayText}</ReactMarkdown>
        </Typography>
      </Box>
    </Box>
  );
};

export default ThoughtItemCommon;
